import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api';
import type { Environment, MerchantProfile, ProviderCode, ProviderConfig } from '../api';
import { usePlainLanguage } from '../plainLanguage';
import {
  ArchiveButton,
  ArchivedToggle,
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  FormGrid,
  Modal,
  ModalActions,
  PageHeadRow,
  Pill,
  StatusPill,
  TableScroll,
  useAction,
  useLoad,
} from '../ui';

const OFFER_UAT = import.meta.env.DEV;

/** Only the fields each provider actually needs; everything is write-only. */
const CREDENTIAL_FIELDS: Record<ProviderCode, { key: string; label: string; optional?: boolean }[]> = {
  bml: [{ key: 'apiKey', label: 'API key' }],
  swipe: [
    { key: 'clientId', label: 'Client ID' },
    { key: 'clientSecret', label: 'Client secret' },
    { key: 'webhookHmacKey', label: 'Webhook signing key (whsec_…)' },
    { key: 'baseUrl', label: 'Base URL', optional: true },
  ],
};

/**
 * Payment providers for one organization.
 *
 * Always org-scoped now: the old page could list every customer's configs at once,
 * which produced several cards all titled "BML Connect" whose only distinguishing
 * mark was a line of grey text.
 */
export function ProvidersSection({ orgId }: { orgId: string }) {
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);

  const configs = useLoad(
    () => api.providerConfigs.list(orgId, showArchived),
    [orgId, showArchived],
  );
  const outlets = useLoad(() => api.outlets.list(orgId), [orgId]);
  const action = useAction();
  const plain = usePlainLanguage();

  return (
    <>
      <div className="mb-3.5">
        <PageHeadRow
          actions={
            <>
              <ArchivedToggle showArchived={showArchived} onChange={setShowArchived} />
              <Button onClick={() => setAdding(true)}>
                {plain ? 'Set up BML Connect' : 'Add provider'}
              </Button>
            </>
          }
        >
          <p className="m-0 max-w-[70ch] text-muted">
            {plain
              ? 'Your BML Connect API key is encrypted when you save it and never shown again. This page can only tell you that one is saved.'
              : 'Credentials are encrypted on save and never come back out — this page can tell you a key exists, never what it is.'}
          </p>
        </PageHeadRow>
      </div>

      <ErrorBanner message={configs.error ?? action.error} />

      <Modal
        open={adding}
        title={plain ? 'BML Connect' : 'Add or update a provider'}
        onClose={() => setAdding(false)}
      >
        <ProviderForm
          orgId={orgId}
          outlets={outlets.data ?? []}
          onSaved={() => {
            setAdding(false);
            configs.reload();
          }}
        />
      </Modal>

      {configs.loading && <Empty>Loading…</Empty>}
      {!configs.loading && configs.data?.length === 0 && (
        <Card>
          <Empty>
            {plain
              ? showArchived
                ? 'Nothing set up, archived or otherwise.'
                : 'BML Connect is not set up yet.'
              : showArchived
                ? 'No providers, archived or otherwise.'
                : 'No provider configured yet.'}
          </Empty>
        </Card>
      )}

      {(configs.data ?? []).map((config) => (
        <ConfigCard
          key={config.id}
          config={config}
          outlets={outlets.data ?? []}
          busy={action.busy}
          onArchive={() =>
            void action.run(() => api.providerConfigs.archive(config.id)).then(configs.reload)
          }
          onRestore={() =>
            void action.run(() => api.providerConfigs.restore(config.id)).then(configs.reload)
          }
        />
      ))}
    </>
  );
}

function ConfigCard({
  config,
  outlets,
  busy,
  onArchive,
  onRestore,
}: {
  config: ProviderConfig;
  outlets: { id: string; name: string }[];
  busy: boolean;
  onArchive: () => void;
  onRestore: () => void;
}) {
  const [profile, setProfile] = useState<MerchantProfile | null>(null);
  const action = useAction();
  const plain = usePlainLanguage();

  const scope = config.outletId
    ? (outlets.find((outlet) => outlet.id === config.outletId)?.name ??
      (plain ? 'one shop' : 'one outlet'))
    : plain
      ? 'All shops'
      : 'Whole organization';

  // The scope is what actually tells two of these apart, so it leads the title.
  const providerName = config.providerCode === 'bml' ? 'BML Connect' : 'Swipe (direct)';

  return (
    <Card
      title={`${scope} — ${providerName}`}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={config.enabled ? 'on' : 'off'}>
            {config.enabled ? (plain ? 'In use' : 'Enabled') : plain ? 'Turned off' : 'Disabled'}
          </Pill>
          <StatusPill status={config.status} />
          <ArchiveButton
            status={config.status}
            label={`${providerName} for ${scope.toLowerCase()}`}
            busy={busy}
            onArchive={onArchive}
            onRestore={onRestore}
          />
        </div>
      }
    >
      <div className="text-muted">
        {plain ? (
          config.hasCredentials ? 'API key saved' : 'No API key saved'
        ) : (
          <>
            {config.environment.toUpperCase()} ·{' '}
            {config.hasCredentials ? 'credentials stored' : 'no credentials'}
          </>
        )}
      </div>

      {/*
        A row can be enabled here and still not reach a till. Swipe is reached through
        BML Connect instead, so the direct integration is parked; saying so on the row
        beats an operator turning it on and finding no tile appears.
      */}
      {config.providerCode === 'swipe' && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-muted">
          <Pill tone="warn">{plain ? 'not in use' : 'not currently offered'}</Pill>
          <span>
            {plain
              ? 'Swipe payments come through BML Connect instead.'
              : 'Swipe is reached as a method of BML Connect. This direct connection is parked and counters will not be offered it.'}
          </span>
        </div>
      )}

      {/* Staff only: it is pasted into Swipe's portal, and that connection is parked. */}
      {config.webhookUrl && !plain && (
        <div className="mt-2.5">
          <label className="mb-1 block text-xs text-muted">
            Webhook URL — paste this into the provider portal
          </label>
          <code className="font-mono text-xs break-all">{config.webhookUrl}</code>
        </div>
      )}

      {config.providerCode === 'bml' && config.status === 'active' && (
        <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
          <Button
            variant="ghost"
            disabled={action.busy}
            onClick={() =>
              void action.run(async () => {
                setProfile(await api.providerConfigs.merchant(config.id));
              })
            }
          >
            {action.busy ? 'Asking BML…' : plain ? 'Test the connection' : 'Check with BML'}
          </Button>
          <span className="text-muted">
            {plain
              ? 'Asks BML whether your saved key works, and which payment apps your account offers.'
              : 'Reads the merchant live, which also proves the stored key still works.'}
          </span>
        </div>
      )}

      <ErrorBanner message={action.error} />
      {profile && <Merchant profile={profile} />}
    </Card>
  );
}

function Merchant({ profile }: { profile: MerchantProfile }) {
  // BML's own code for each method ("bml_mobilepay") is for staff; its description names it.
  const plain = usePlainLanguage();
  return (
    <div className="mt-3.5 border-t border-divider pt-3.5">
      <h3 className="font-semibold">{profile.tradingName || '(no trading name)'}</h3>
      <div className="text-muted">
        {profile.registeredName}
        {profile.companyNumber && ` · ${profile.companyNumber}`}
        {profile.currencies.length > 0 && ` · ${profile.currencies.join(', ')}`}
        {profile.reviewStatus && (
          <>
            {' '}
            <Pill tone={profile.reviewStatus === 'verified' ? 'on' : 'warn'}>
              {profile.reviewStatus}
            </Pill>
          </>
        )}
      </div>

      <TableScroll>
        <table className="mt-3">
          <thead>
            <tr>
              {!plain && <th>BML provider</th>}
              <th>{plain ? 'Payment method' : 'Description'}</th>
              <th>{plain ? 'At your counters' : 'On a customer display'}</th>
            </tr>
          </thead>
          <tbody>
            {profile.providers.map((provider) => (
              <tr key={provider.value}>
                {!plain && <td className="font-mono text-xs break-all">{provider.value}</td>}
                <td className={plain ? undefined : 'text-muted'}>{provider.description}</td>
                <td>
                  {provider.status === 'supported' && <Pill tone="on">{provider.displayName}</Pill>}
                  {/* Out of scope by decision: this product is scan-to-pay, and the till
                      already has its own path for card tenders. */}
                  {provider.status === 'card' && (
                    <Pill tone="off">
                      {plain ? 'card — use the card machine' : 'card — not this product'}
                    </Pill>
                  )}
                  {/* Switched on for this merchant and never handled by us. Says nobody
                      has looked, not that it cannot work. */}
                  {provider.status === 'unknown' && (
                    <Pill tone="warn">{plain ? 'not offered yet' : 'not supported yet'}</Pill>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </div>
  );
}

function ProviderForm({
  orgId,
  outlets,
  onSaved,
}: {
  orgId: string;
  outlets: { id: string; name: string }[];
  onSaved: () => void;
}) {
  const [providerCode, setProviderCode] = useState<ProviderCode>('bml');
  const [environment, setEnvironment] = useState<Environment>('prod');
  const [outletId, setOutletId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const action = useAction();
  const plain = usePlainLanguage();

  async function submit(event: FormEvent) {
    event.preventDefault();

    // Blank fields mean "leave the stored value alone", so they must not be sent.
    const filled = Object.fromEntries(
      Object.entries(credentials).filter(([, value]) => value.trim().length > 0),
    );

    const ok = await action.run(() =>
      api.providerConfigs.upsert({
        orgId,
        outletId: outletId || undefined,
        providerCode,
        enabled,
        environment,
        credentials: Object.keys(filled).length > 0 ? filled : undefined,
      }),
    );

    if (ok) {
      setCredentials({});
      onSaved();
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <ErrorBanner message={action.error} />

      <FormGrid>
        {/* One choice is no choice: a merchant is told it is BML Connect by the title. */}
        {!plain && (
          <Field label="Provider">
            <select
              value={providerCode}
              onChange={(event) => {
                setProviderCode(event.target.value as ProviderCode);
                setCredentials({});
              }}
            >
              <option value="bml">BML Connect</option>
              {/*
                Swipe (direct) is deliberately not offered: it is reached as a method of
                BML Connect, so a shop keeps one set of credentials and one
                reconciliation path. Restoring it is this option plus emptying
                ProviderRegistry.Suppressed. Existing rows still load and display.
              */}
            </select>
          </Field>
        )}
        {/*
          UAT is offered only under the Vite dev server. The built console - the one
          nginx serves in production - saves every config as production: merchants have
          no use for BML's test bank, and a config left on it points live keys at the
          wrong bank. The API refuses UAT in production too (provider-environment.ts).
        */}
        {OFFER_UAT && (
          <Field label="Environment">
            <select
              value={environment}
              onChange={(event) => setEnvironment(event.target.value as Environment)}
            >
              <option value="prod">Production</option>
              <option value="uat">UAT</option>
            </select>
          </Field>
        )}
        <Field label={plain ? 'Use for' : 'Scope'}>
          <select value={outletId} onChange={(event) => setOutletId(event.target.value)}>
            <option value="">{plain ? 'All shops' : 'Whole organization'}</option>
            {outlets.map((outlet) => (
              <option key={outlet.id} value={outlet.id}>
                {outlet.name}
              </option>
            ))}
          </select>
        </Field>
      </FormGrid>

      <FormGrid>
        {CREDENTIAL_FIELDS[providerCode].map((field) => (
          <Field key={field.key} label={field.label}>
            <input
              type="password"
              autoComplete="off"
              placeholder={
                field.optional
                  ? 'optional'
                  : plain
                    ? 'leave blank to keep the saved one'
                    : 'leave blank to keep'
              }
              value={credentials[field.key] ?? ''}
              onChange={(event) =>
                setCredentials({ ...credentials, [field.key]: event.target.value })
              }
            />
          </Field>
        ))}
      </FormGrid>

      <label className="m-0 inline-flex items-center gap-[7px] text-[13px] text-muted">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        {plain ? 'Take BML Connect payments at the counters' : 'Offer this provider to counters'}
      </label>

      <ModalActions>
        <Button type="submit" disabled={action.busy}>
          {action.busy ? 'Saving…' : plain ? 'Save' : 'Save provider'}
        </Button>
      </ModalActions>
    </form>
  );
}
